import {
  CollectiveProposalQueryResult,
  CollectiveProposalStatus,
  CollectiveQueryResult,
  useThreadChangedSubscription,
  useThreadQuery
} from 'graphql/generated'
import Autolink from 'components/Autolink'
import ProposalTimingInfo from './ProposalTimingInfo'
import ProposalStatusBadge from './ProposalStatusBadge'
import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faPoll } from '@fortawesome/pro-regular-svg-icons'
import { ProposalSurveyQuestions } from './CollectiveProposalSurveyQuestions'
import ProposalMoreActionsButton from './ProposalMoreActionsButton'
import CollectiveUserPopover from '../Common/CollectiveUserPopover'
import SurveyVotes from './SurveyVotes/SurveyVotes'
import CollectivePageHeader from '../Common/CollectivePageHeader/CollectivePageHeader'
import { humanTime } from 'lib/datetime'
import ProposalTitle from './CollectiveProposalTitle/ProposalTitle'
import ContentWrapper from '../../ContentWrapper'
import { ForumProvider } from '../DiscussionBoard/ForumContext'
import ThreadComments from '../DiscussionBoard/ThreadComments'

export const getProposalTitle = (
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
) => {
  return proposal.isShortForm
    ? `Poll: ${proposal.survey?.questions[0].question}`
    : proposal.title
}

export const CollectiveProposalSurvey: React.FC<{
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
  collective: NonNullable<CollectiveQueryResult['collective']>
}> = ({ proposal, collective }) => {
  const [showingPage, setShowingPage] = useState(0)
  const alreadyAnswered = proposal.survey?.isAnswered || false

  const { data } = useThreadQuery({
    variables: {
      threadId: proposal.thread?.id || '',
      collaboratorConnection: {
        first: 0
      }
    },
    skip: !proposal.thread?.id
  })

  useThreadChangedSubscription({
    variables: {
      threadId: proposal.thread?.id || ''
    },
    skip: !proposal.thread
  })

  if (!proposal.survey) {
    return null
  }

  const thread = data?.thread
  const isBotProposal = proposal.proposedBy.user.platformUser?.isBot

  return (
    <ContentWrapper>
      <CollectivePageHeader collective={collective} />

      <div className="p-4 space-y-4">
        <div className={'rounded-[10px] p-5 bg-gray-50'}>
          <div className="flex flex-1 mb-[5px] justify-between">
            <ProposalTitle icon={faPoll} title={getProposalTitle(proposal)} />

            <div className="ml-2 flex gap-1 items-start">
              {alreadyAnswered && (
                <div className="bg-gray-100 border-[1px] border-gray-300 py-2 px-3 rounded-md">
                  <div className="text-sm font-normal color-gray-700">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    {`You answered`}
                  </div>
                </div>
              )}

              <ProposalMoreActionsButton
                collective={collective}
                proposal={proposal}
              />
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2 justify-between">
            <ProposalStatusBadge proposal={proposal} />
          </div>

          <div className="pt-3 flex flex-row items-center space-x-2">
            {!isBotProposal && (
              <>
                <p className="text-xs text-gray-400">{`Asked By: `}</p>
                <CollectiveUserPopover user={proposal.proposedBy.user} />
              </>
            )}

            <p className="text-xs text-gray-400">{`\u00b7 \u2004 ${humanTime(
              proposal.createdAt
            )}`}</p>
          </div>
        </div>

        <div className="flex flex-col space-y-2 pb-5 mt-4">
          {!!proposal.descriptionHtml && (
            <div>
              <p className="text-gray-400 text-sm font-light">
                Proposed details:
              </p>

              <div className="prose">
                <Autolink as="div" newWindow lightWeight>
                  {proposal.descriptionHtml}
                </Autolink>
              </div>
            </div>
          )}

          <div>
            <ProposalSurveyQuestions
              proposal={proposal}
              showingPage={showingPage}
              setShowingPage={setShowingPage}
              limitedView={false}
            />
          </div>

          {proposal.status === CollectiveProposalStatus.ACTIVE && (
            <div>
              <p className="text-gray-400 text-sm font-light">Timing:</p>

              <div>
                <ProposalTimingInfo proposal={proposal} />
              </div>
            </div>
          )}
        </div>

        {thread && (
          <ForumProvider selectedCategoryId={thread.category.id}>
            <ThreadComments thread={thread} />
          </ForumProvider>
        )}
      </div>

      <div className="mx-4 pb-5">
        <SurveyVotes proposal={proposal} />
      </div>
    </ContentWrapper>
  )
}
